import { Dispatch, SetStateAction, useMemo } from "react";
import { Alert } from "reactstrap";

import useSelectableReferences from "../../ReferencesHooks/useSelectableReferences";
import { AvailableModuleData } from "../../ReferencesHooks/useAvailableModules";
import { UserRecordActivity } from "../../../../../types/GlobalInterfaces";
import SkeletonLoader from "../../../../../elements/SkeletonLoader";
import { Field } from "../../../../../types/TenantInterfaces";
import IconButton from "../../../../../elements/IconButton";
import { Tabs } from "./SelectableReferences";
import ReferencesList from "./ReferencesList";

const tabsHeaderMap = {
  All: "All Records",
  Recent: "Recently Used",
  RecentlyViewed: "Recently Viewed",
  Favorites: "My Favorites",
};

interface AllReferencesProps {
  records: any[];
  currentKey: string;
  modulesMap: Map<string, AvailableModuleData>;
  field: Field;
  recordsLoading: boolean;
  selected: any;
  setSelected: Dispatch<SetStateAction<any>>;
  setKeepOpen: Dispatch<SetStateAction<boolean>>;
  setHideReference: Dispatch<SetStateAction<boolean>>;
  oneTap: boolean;
  onClose: () => void;
  isSubitemReference: boolean;
  selectedSubitemParent: any;
  setSelectedSubitemParent: Dispatch<SetStateAction<any>>;
  tabs: { title: string; value: Tabs }[];
  setTab: Dispatch<SetStateAction<Tabs>>;
  selectedTab: Tabs;
  userRecordActivity: UserRecordActivity;
  setShowFieldSettings: null | Dispatch<SetStateAction<boolean>>;
  showDisplayButton?: boolean;
}

const Title = ({
  css = "",
  subitemName,
  selectedSubitemParent,
  setShowFieldSettings,
  isSubitem,
  title,
}: {
  css?: string;
  subitemName: string | undefined;
  selectedSubitemParent: any;
  setShowFieldSettings: null | Dispatch<SetStateAction<boolean>>;
  isSubitem?: boolean;
  title: string;
}) => {
  return (
    <div className="tw:flex tw:justify-between tw:mx-1">
      <h5 className={css}>{title}</h5>
      {setShowFieldSettings && !isSubitem && (
        <IconButton
          icon="mdi mdi-cog"
          onClick={() => setShowFieldSettings(true)}
          color="secondary"
          size="sm"
          outline={true}
          className="tw:mr-3"
        />
      )}
    </div>
  );
};

const AllReferences = ({
  records,
  currentKey,
  modulesMap,
  recordsLoading,
  field,
  selected,
  setSelected,
  setKeepOpen,
  setHideReference,
  oneTap,
  onClose,
  isSubitemReference,
  selectedSubitemParent,
  setSelectedSubitemParent,
  tabs,
  setTab,
  selectedTab,
  userRecordActivity,
  setShowFieldSettings,
  showDisplayButton,
}: AllReferencesProps) => {
  const module = modulesMap?.get(currentKey);
  const subitemField = module?.SubitemSchema;
  const subitemName = subitemField?.Name;
  const { isRecordSelected, handleSelectRecord } = useSelectableReferences({
    field,
    selected,
    setSelected,
    oneTap,
    onClose,
    selectedSubitemParent,
    subitemField,
  });

  const favorites = useMemo(() => {
    return userRecordActivity?.[currentKey]?.Favorites;
  }, [currentKey, userRecordActivity]);

  if (recordsLoading) {
    return (
      <div className="mx-3">
        <Title
          title={tabsHeaderMap[selectedTab]}
          subitemName={subitemName}
          selectedSubitemParent={selectedSubitemParent}
          setShowFieldSettings={null}
        />
        <SkeletonLoader css="mb-2" />
        <SkeletonLoader css="mb-2" />
        <SkeletonLoader css="mb-2" />
      </div>
    );
  }

  if (subitemField?.FieldType === "Subitem") {
    if (records && !Array.isArray(records)) {
      records = [records];
    }
  }

  return (
    <>
      <div className="tw:inline-flex tw:pl-3 tw:py-2">
        {tabs?.map((t) => {
          return (
            <div
              key={t.value}
              className={`tw:px-3 tw:mx-1 tw:text-dark tw:cursor-pointer`}
              style={{
                backgroundColor:
                  selectedTab === t.value ? "#c8f3d8" : "#e8edea",
                borderRadius: 4,
              }}
              onClick={() => setTab(t.value)}
            >
              {t.title}
            </div>
          );
        })}
      </div>

      <Title
        css="tw:px-3 tw:pt-2"
        subitemName={subitemName}
        selectedSubitemParent={selectedSubitemParent}
        setShowFieldSettings={setShowFieldSettings}
        isSubitem={!!subitemField}
        title={tabsHeaderMap[selectedTab]}
      />
      <>
        {!records?.length ? (
          <Alert color="superlight" className="p-2 px-4 mx-2">
            No Records Found.
          </Alert>
        ) : (
          <div
            className="px-2"
            style={{ maxHeight: 300, overflowY: "auto", overflowX: "hidden" }}
          >
            <ReferencesList
              records={records}
              modulesMap={modulesMap}
              isRecordSelected={isRecordSelected}
              handleSelectRecord={handleSelectRecord}
              handleOpen={handleOpen}
              handleClose={handleClose}
              setKeepOpen={setKeepOpen}
              isSubitemReference={isSubitemReference}
              subitemField={subitemField}
              currentKey={currentKey}
              selectedSubitemParent={selectedSubitemParent}
              setSelectedSubitemParent={setSelectedSubitemParent}
              favorites={favorites}
              showDisplayButton={showDisplayButton}
            />
          </div>
        )}
      </>
    </>
  );

  function handleOpen() {
    setKeepOpen(true);
    setHideReference(true);
  }

  function handleClose() {
    setKeepOpen(false);
    setHideReference(false);
  }
};

export default AllReferences;
